import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { adminQueryKeys } from '@/hooks/admin/query-keys';
import { retryControlApi } from '@/hooks/admin/retry';
import { meshOutboxService } from '@/services/control/mesh-outbox-service';
import type {
  MeshOutboxGlobal,
  MeshOutboxQueueStats,
} from '@/types/control/mesh-outbox';

/**
 * The worker writes a snapshot every 5s, so polling faster buys nothing and
 * polling slower makes a stalled worker look current for longer.
 *
 * `refetchIntervalInBackground` is deliberately not set: react-query gates the
 * timer on `refetchIntervalInBackground || focusManager.isFocused()`, and the
 * focus manager tracks `visibilitychange` — so leaving it undefined is what
 * stops the page polling behind a hidden tab. No custom visibility hook needed.
 */
const SNAPSHOT_POLL_MS = 5_000;

export function useMeshOutboxGlobal(): UseQueryResult<MeshOutboxGlobal, Error> {
  return useQuery<MeshOutboxGlobal, Error>({
    queryKey: adminQueryKeys.meshOutboxGlobal(),
    queryFn: ({ signal }) => meshOutboxService.getGlobal(signal),
    refetchInterval: SNAPSHOT_POLL_MS,
    retry: retryControlApi,
  });
}

export function useMeshOutboxTenants(): UseQueryResult<
  MeshOutboxQueueStats[],
  Error
> {
  return useQuery<MeshOutboxQueueStats[], Error>({
    queryKey: adminQueryKeys.meshOutboxTenants(),
    queryFn: ({ signal }) => meshOutboxService.listTenants(signal),
    refetchInterval: SNAPSHOT_POLL_MS,
    retry: retryControlApi,
  });
}
